import { useState, useCallback, useRef, useEffect } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import {
  useReorderGoalsMutation,
  useUpdateGoalMutation,
} from './useGoalMutation';

/**
 * Handle moving goals between different years
 */
const handleCrossYearMove = (
  goals,
  dragIndex,
  hoverIndex,
  sourceYear,
  targetYear,
) => {
  const sourceYearGoals = goals.filter(g => g.planYear === sourceYear);
  const targetYearGoals = goals.filter(g => g.planYear === targetYear);

  const sourceGoal = sourceYearGoals[dragIndex];
  if (!sourceGoal) return goals;

  // Remove from source year and update planYear
  const sourceGoalIndex = goals.findIndex(g => g.id === sourceGoal.id);
  if (sourceGoalIndex === -1) return goals;

  const draggedGoal = { ...sourceGoal, planYear: targetYear };

  // Remove the original goal
  const goalsWithoutDragged = goals.filter(
    (_, index) => index !== sourceGoalIndex,
  );

  // Find insertion point in target year
  let insertIndex;
  if (targetYearGoals.length === 0 || hoverIndex === 0) {
    // Insert at beginning of target year
    const firstTargetYearIndex = goalsWithoutDragged.findIndex(
      g => g.planYear === targetYear,
    );
    insertIndex =
      firstTargetYearIndex === -1
        ? goalsWithoutDragged.length
        : firstTargetYearIndex;
  } else {
    // Insert at specific position within target year
    // hoverIndex - 1 because we want to insert BEFORE the goal at hoverIndex
    const targetGoalIndex = Math.min(
      hoverIndex - 1,
      targetYearGoals.length - 1,
    );
    const targetGoal = targetYearGoals[targetGoalIndex];
    const actualTargetIndex = goalsWithoutDragged.findIndex(
      g => g.id === targetGoal.id,
    );
    insertIndex = actualTargetIndex + 1; // Insert AFTER the previous goal
  }

  // Insert at calculated position
  return [
    ...goalsWithoutDragged.slice(0, insertIndex),
    draggedGoal,
    ...goalsWithoutDragged.slice(insertIndex),
  ];
};

/**
 * Build reorder data for API call
 */
const buildReorderData = localGoals => {
  const validGoals = localGoals.filter(
    goal => goal.planYear !== null && goal.planYear !== undefined,
  );

  return validGoals.map(goal => ({
    id: goal.id,
    plan_year: goal.planYear,
  }));
};

/**
 * Detect year changes between original and local goals
 */
const detectYearChanges = (localGoals, originalGoals) => {
  return localGoals
    .map(localGoal => {
      const originalGoal = originalGoals.find(g => g.id === localGoal.id);
      if (originalGoal && originalGoal.planYear !== localGoal.planYear) {
        return {
          id: localGoal.id,
          plan_year: localGoal.planYear,
        };
      }

      return null;
    })
    .filter(Boolean);
};

/**
 * Custom hook to handle goal drag and drop operations
 * Separates DnD logic from component rendering logic
 */
export const useGoalDragDrop = (goals, setRecentlyMovedGoalId) => {
  const { reorderGoalsMutation } = useReorderGoalsMutation();
  const { updateGoalMutation } = useUpdateGoalMutation();

  const [localGoals, setLocalGoals] = useState([]);
  const [activeGoal, setActiveGoal] = useState(null);
  const [currentTargetPosition, setCurrentTargetPosition] = useState(null);
  const goalsRef = useRef(goals);
  const currentTargetRef = useRef(null);

  // Update local goals when server goals change
  useEffect(() => {
    if (goals) {
      setLocalGoals([...goals]);
      goalsRef.current = goals;
    }
  }, [goals]);

  // Handle moving goals within same year or between years
  const moveGoal = useCallback(
    (dragIndex, hoverIndex, sourceYear, targetYear) => {
      if (dragIndex === hoverIndex && sourceYear === targetYear) {
        return;
      }

      setLocalGoals(currentGoals => {
        if (sourceYear === targetYear) {
          const yearGoals = currentGoals.filter(g => g.planYear === sourceYear);
          const reorderedYearGoals = arrayMove(
            yearGoals,
            dragIndex,
            hoverIndex,
          );

          const result = currentGoals.map(goal => {
            if (goal.planYear === sourceYear) {
              const yearIndex = yearGoals.findIndex(g => g.id === goal.id);
              return reorderedYearGoals[yearIndex];
            }
            return goal;
          });

          return result;
        }

        // Cross-year move
        const sourceYearGoals = currentGoals.filter(
          g => g.planYear === sourceYear,
        );

        if (dragIndex >= sourceYearGoals.length) {
          return currentGoals;
        }

        const result = handleCrossYearMove(
          currentGoals,
          dragIndex,
          hoverIndex,
          sourceYear,
          targetYear,
        );

        return result;
      });
    },
    [],
  );

  // Handle drag end - sync with backend
  const handleDragEnd = useCallback(() => {
    setLocalGoals(currentLocalGoals => {
      const reorderData = buildReorderData(currentLocalGoals);
      const yearChanges = detectYearChanges(
        currentLocalGoals,
        goalsRef.current,
      );

      // Send reorder request if there are goals to reorder
      if (reorderData.length > 0) {
        reorderGoalsMutation(reorderData);
      }

      // Send update requests for year changes
      yearChanges.forEach(updateData => {
        updateGoalMutation(updateData);
      });

      return currentLocalGoals;
    });
  }, [reorderGoalsMutation, updateGoalMutation]);

  const handleDragStart = useCallback(event => {
    const { active } = event;
    const activeData = active.data.current;

    currentTargetRef.current = null; // Reset target tracking
    setCurrentTargetPosition(null); // Reset target position

    if (activeData?.type === 'goal') {
      setActiveGoal({
        ...activeData.goal,
        originalIndex: activeData.index,
        originalYear: activeData.year,
      });
    }
  }, []);

  const handleDragOver = useCallback(
    event => {
      const { active, over } = event;

      if (!active || !over) return;

      const activeData = active.data.current;
      const overData = over.data.current;

      if (!activeData || !overData) return;

      // Only handle goal drops
      if (activeData.type !== 'goal') return;

      const activeIndex = activeData.index;
      const activeYear = activeData.year;

      let targetIndex, targetYear;

      if (overData.type === 'goal') {
        // Dropped on another goal
        targetIndex = overData.index;
        targetYear = overData.year;
      } else if (overData.type === 'year-table') {
        // Dropped on empty year table
        targetIndex = 0;
        targetYear = overData.year;
      } else {
        return;
      }

      // Store the target information for handleDragEndEvent
      currentTargetRef.current = {
        activeIndex,
        targetIndex,
        sourceYear: activeYear,
        targetYear,
      };

      setCurrentTargetPosition({
        year: targetYear,
        index: targetIndex,
      });

      // Only perform cross-year moves immediately
      // Same-year moves will be handled in handleDragEndEvent
      if (activeYear !== targetYear) {
        moveGoal(activeIndex, targetIndex, activeYear, targetYear);
      }
    },
    [moveGoal],
  );

  const handleDragEndEvent = useCallback(
    event => {
      const { active, over } = event;

      const targetInfo = currentTargetRef.current;
      currentTargetRef.current = null; // Reset target tracking
      setCurrentTargetPosition(null); // Reset target position
      setActiveGoal(null);

      if (!over || active.id === over.id || !targetInfo) return;

      // Perform the actual move for same-year moves only
      // Cross-year moves already happened in handleDragOver
      const { activeIndex, targetIndex, sourceYear, targetYear } = targetInfo;

      let draggedGoalId = active.id;

      if (sourceYear === targetYear && activeIndex !== targetIndex) {
        moveGoal(activeIndex, targetIndex, sourceYear, targetYear);

        // Backend sync after the move
        setTimeout(() => {
          handleDragEnd();
        }, 50);

        // Trigger animation for same-year reordering
        if (setRecentlyMovedGoalId) {
          setTimeout(() => {
            setRecentlyMovedGoalId(draggedGoalId);
            // Clear the highlight after 3 seconds
            setTimeout(() => {
              setRecentlyMovedGoalId(null);
            }, 3000);
          }, 300);
        }
      } else if (sourceYear !== targetYear) {
        // Backend sync for cross-year moves
        setTimeout(() => {
          handleDragEnd();
        }, 50);

        // Trigger animation for cross-year moves
        if (setRecentlyMovedGoalId) {
          setTimeout(() => {
            setRecentlyMovedGoalId(draggedGoalId);
            // Clear the highlight after 3 seconds
            setTimeout(() => {
              setRecentlyMovedGoalId(null);
            }, 3000);
          }, 300);
        }
      }
    },
    [moveGoal, handleDragEnd, setRecentlyMovedGoalId],
  );

  return {
    localGoals,
    activeGoal,
    currentTargetPosition,
    handleDragStart,
    handleDragOver,
    handleDragEndEvent,
  };
};
